import React, { useState, useRef, useEffect, useId } from 'react';
import styles from './select.module.scss';

export interface SelectOption {
  value: string;
  label: string;
  description?: string;
}

type SelectVariant = 'default' | 'solid' | 'light' | 'compact';

// Mirrors .dropdownMenu's max-height (18.75rem) — the space the menu wants below the field.
const MENU_MAX_HEIGHT = 300;

export interface SelectProps {
  options: SelectOption[];
  placeholder?: string;
  value?: string;
  onChange?: (value: string) => void;
  name?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  className?: string;
  variant?: SelectVariant | SelectVariant[];
  label?: string;
  emptyMessage?: string;
}

export const Select: React.FC<SelectProps> = ({
  options,
  placeholder,
  value,
  onChange,
  name,
  required = false,
  disabled = false,
  error,
  className = '',
  variant = 'default' as SelectVariant | SelectVariant[],
  label,
  emptyMessage = 'No options available',
}) => {
  const s = styles as Record<string, string>;
  const variantClasses = (Array.isArray(variant) ? variant : [variant]).map(v => s[v]).join(' ');

  const [isOpen, setIsOpen] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const listboxId = useId();

  const selectedOption = options.find((opt) => opt.value === value);
  const selectedIndex = options.findIndex((opt) => opt.value === value);

  const getInitialFocusIndex = () => (selectedIndex >= 0 ? selectedIndex : -1);

  // Open above the field when the menu would not fit below it, and there is more room up there.
  const resolvePlacement = () => {
    const rect = dropdownRef.current?.getBoundingClientRect();
    if (!rect) return;

    const spaceBelow = window.innerHeight - rect.bottom;
    setOpenUpward(spaceBelow < MENU_MAX_HEIGHT && rect.top > spaceBelow);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setFocusedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (option: SelectOption) => {
    onChange?.(option.value);
    setIsOpen(false);
    setFocusedIndex(-1);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;

    if (!isOpen && (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      e.preventDefault();
      resolvePlacement();
      setIsOpen(true);
      setFocusedIndex(getInitialFocusIndex());
      return;
    }

    if (isOpen) {
      switch (e.key) {
        case 'Escape':
          e.preventDefault();
          setIsOpen(false);
          setFocusedIndex(-1);
          break;
        case 'ArrowDown':
          e.preventDefault();
          setFocusedIndex((prev) => (prev < options.length - 1 ? prev + 1 : prev));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setFocusedIndex((prev) => (prev > 0 ? prev - 1 : prev));
          break;
        case 'Enter':
        case ' ':
          e.preventDefault();
          if (focusedIndex >= 0 && focusedIndex < options.length) {
            handleSelect(options[focusedIndex]);
          }
          break;
        case 'Tab':
          setIsOpen(false);
          setFocusedIndex(-1);
          break;
      }
    }
  };

  const handleToggle = () => {
    if (!disabled) {
      if (!isOpen) {
        resolvePlacement();
        setFocusedIndex(getInitialFocusIndex());
      }
      setIsOpen(!isOpen);
    }
  };

  return (
    <div className={`${styles.selectWrapper} ${variantClasses} ${className}`} ref={dropdownRef}>
      {label && <label className={styles.label}>{label}</label>}

      {/* Hidden input for form compatibility */}
      <input type="hidden" name={name} value={value || ''} required={required} />

      <div
        className={`${styles.customSelect} ${variantClasses} ${isOpen ? styles.customSelectOpen : ''} ${error ? styles.error : ''} ${disabled ? styles.disabled : ''}`}
        onClick={handleToggle}
        tabIndex={disabled ? -1 : 0}
        onKeyDown={handleKeyDown}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-controls={listboxId}
        aria-label={label || placeholder}
        aria-activedescendant={focusedIndex >= 0 ? `${listboxId}-option-${focusedIndex}` : undefined}
        aria-disabled={disabled}
      >
        <div className={`${styles.selectLabel} ${!selectedOption ? styles.selectLabelPlaceholder : ''}`}>
          {selectedOption ? selectedOption.label : placeholder}
        </div>
        <span className={`${styles.chevron} ${isOpen ? styles.chevronOpen : ''}`} aria-hidden="true">
          ›
        </span>
      </div>

      {isOpen && (
        <div
          className={`${styles.dropdownMenu} ${openUpward ? styles.dropdownMenuUp : ''}`}
          id={listboxId}
          role="listbox"
          aria-label={label || placeholder}
        >
          {options.length === 0 ? (
            <div className={styles.dropdownEmpty}>{emptyMessage}</div>
          ) : (
            options.map((option, index) => (
              <div
                key={option.value}
                id={`${listboxId}-option-${index}`}
                className={`${styles.dropdownItem} ${value === option.value ? styles.dropdownItemSelected : ''} ${focusedIndex === index ? styles.dropdownItemFocused : ''}`}
                onClick={() => handleSelect(option)}
                role="option"
                aria-selected={value === option.value}
              >
                <div className={styles.dropdownItemLabel}>{option.label}</div>
                {option.description && (
                  <div className={styles.dropdownItemDescription}>{option.description}</div>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {error && <span className={styles.errorMessage}>{error}</span>}
    </div>
  );
};